import React, { useState } from 'react';

const urlRegex = /(https?:\/\/[^\s*]+)/g;

function cleanUrl(url) {
  return url.replace(/^[*]+/, '').replace(/[*]+$/, '');
}

function detectStore(url, content) {
  const target = `${url} ${content}`.toLowerCase();
  if (target.includes('amazon') || target.includes('amzn.to')) {
    return { name: 'Amazon', class: 'amazon' };
  }
  if (target.includes('flipkart') || target.includes('fkrt.it')) {
    return { name: 'Flipkart', class: 'flipkart' };
  }
  if (target.includes('myntra')) {
    return { name: 'Myntra', class: 'myntra' };
  }
  if (target.includes('meesho')) {
    return { name: 'Meesho', class: 'meesho' };
  }
  if (target.includes('ajio')) {
    return { name: 'Ajio', class: 'default' };
  }
  return { name: 'Verified Deal', class: 'default' };
}

function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function renderTextWithMarkdown(text, keyPrefix) {
  if (!text) return null;

  const boldRegex = /\*\*([^*]+)\*\*/g;
  const parts = text.split(boldRegex);

  return parts.map((part, index) => {
    if (index % 2 === 1) {
      return <strong key={`${keyPrefix}-b-${index}`}>{part}</strong>;
    }
    const cleaned = part.replace(/^\*{1,2}/, '').replace(/\*{1,2}$/, '');
    return cleaned;
  });
}

function renderLineWithLinks(line, lineIndex) {
  if (!line || !line.trim()) return null;

  const parts = line.split(urlRegex);

  return parts.map((part, partIndex) => {
    if (part.match(urlRegex)) {
      const href = cleanUrl(part);
      return (
        <a
          key={`link-${lineIndex}-${partIndex}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {href}
        </a>
      );
    }
    return renderTextWithMarkdown(part, `txt-${lineIndex}-${partIndex}`);
  });
}

const DealCard = ({ deal }) => {
  const [copied, setCopied] = useState(false);

  // Extract primary deal link for the main CTA button
  const urlsInContent = (deal.content || '').match(urlRegex) || [];
  const primaryUrl = urlsInContent.length > 0 ? cleanUrl(urlsInContent[0]) : null;
  const store = detectStore(primaryUrl || '', deal.content || '');
  const relativeTime = formatRelativeTime(deal.created_at);

  const handleCopyLink = () => {
    if (primaryUrl) {
      navigator.clipboard.writeText(primaryUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <article className="deal-card">
      <div className="deal-card-header">
        <span className={`store-badge ${store.class}`}>
          {store.name}
        </span>
        <span className="deal-time" title={new Date(deal.created_at).toLocaleString()}>
          {relativeTime}
        </span>
      </div>

      {deal.type === 'image' && deal.image_url ? (
        <div className="deal-image-wrapper">
          <img
            src={deal.image_url}
            alt={deal.content || 'Deal image'}
            className="deal-image"
            loading="lazy"
            onError={(e) => {
              // Hide image container on broken image link
              e.currentTarget.parentElement.style.display = 'none';
            }}
          />
        </div>
      ) : null}

      <div className="deal-card-body">
        <div className="deal-content">
          {(deal.content || '').split('\n').map((line, index) => {
            const content = renderLineWithLinks(line, index);
            return content ? <p key={index}>{content}</p> : null;
          })}
        </div>

        {primaryUrl && (
          <div className="deal-card-footer">
            <a
              href={primaryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-grab-deal"
            >
              Grab Deal
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>

            <button
              className="btn-icon-action"
              onClick={handleCopyLink}
              title={copied ? 'Copied link!' : 'Copy deal link'}
              aria-label="Copy deal link"
            >
              {copied ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

export default DealCard;
